import { Location } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import { OSM, XYZ } from 'ol/source';
import { fromLonLat } from 'ol/proj';
import { DragRotateAndZoom, defaults as defaultInteractions } from 'ol/interaction';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { MapService } from 'src/app/Service/map.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ReportContext, ReportDefinition } from '../../report.models';
import { ChartReportApiService } from './chart-report-api.service';
import { ChartReportRow } from './chart-report.models';
import { chartLabel, chartPosition, chartValue } from './chart-report.utils';

@Component({ selector: 'app-chart-report', templateUrl: './chart-report.component.html' })
export class ChartReportComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() context!: ReportContext;
  @Input() definition!: ReportDefinition;
  @ViewChild('canvas') canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('mapTarget') mapTarget!: ElementRef<HTMLDivElement>;
  rows: ChartReportRow[] = [];
  loading = false;
  hasError = false;
  errorMessage = '';
  private chart?: Chart<'line', (number | null)[], string>;
  private map?: Map;
  private resizeObserver?: ResizeObserver;
  private ready = false;
  private destroyed = false;
  private version = 0;
  constructor(private readonly api: ChartReportApiService, private readonly guard: GurdServiceService,
    private readonly notify: NotificationService, private readonly other: OtherService, private readonly router: Router,
    private readonly location: Location, private readonly mapService: MapService, private readonly changeDetector: ChangeDetectorRef) {
    Chart.register(zoomPlugin);
  }
  get hasMap(): boolean { return this.context.type !== '15-5'; }
  get subtitle(): string {
    const endDate = this.context.endDate?.trim();
    return `از تاریخ ${this.context.startDate}${endDate ? ` الی ${endDate}` : ''}`;
  }
  ngOnChanges(): void {
    if (!this.context || !this.definition) return;
    ++this.version; this.loading = false; this.rows = []; this.disposeVisualization(); void this.loadReport();
  }
  ngAfterViewInit(): void { this.ready = true; if (!this.loading && this.rows.length) this.render(); }
  ngOnDestroy(): void { this.destroyed = true; ++this.version; this.disposeVisualization(); }
  goBack(): void { this.location.back(); }
  resetZoom(): void { this.chart?.resetZoom(); }
  async loadReport(): Promise<void> {
    if (this.loading || this.destroyed) return;
    const version = ++this.version; this.loading = true; this.hasError = false; this.errorMessage = ''; this.rows = [];
    this.disposeVisualization();
    try {
      const result = await this.api.load(this.definition.endpoint!, {
        iSystemUser: this.guard.decrypt(localStorage.getItem('ID')!), strSession: this.guard.decrypt(localStorage.getItem('Session')!),
        strUnitID: this.context.id, strDate: this.context.startDate, strSDate: this.context.startDate, strEDate: this.context.endDate,
        strSTime: this.context.startTime, strETime: this.context.endTime,
      });
      if (version !== this.version) return;
      if (!Array.isArray(result)) {
        if (result.ISSessionExpired || result.ISessionExpired) {
          this.notify.showError('لطفا وارد سیستم شوید'); this.other.removeLocalStorage();
          void this.router.navigate(['login'], { queryParams: { report: 'speed-car' } }); return;
        }
        throw new Error(result.strError || 'دریافت اطلاعات با خطا مواجه شد');
      }
      this.rows = result;
      this.loading = false;
      this.changeDetector.detectChanges();
      if (this.ready && this.rows.length) this.render();
    } catch (error) {
      if (version !== this.version) return;
      this.disposeVisualization(); this.rows = []; this.hasError = true;
      this.errorMessage = error instanceof Error ? error.message : 'دریافت اطلاعات ناموفق بود';
    } finally { if (version === this.version) this.loading = false; }
  }
  private render(): void {
    this.disposeVisualization();
    const acceleration = this.context.type === '15-3';
    if (this.hasMap) {
      const config = window as Window & { bOfflineMap?: boolean; strMapUrl?: string };
      this.map = new Map({
        target: this.mapTarget.nativeElement,
        interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
        layers: [new TileLayer({ source: config.bOfflineMap ? new XYZ({ url: config.strMapUrl, attributions: 'Map data © RayanAndishehNasr' }) : new OSM({}) })],
        view: new View({ center: fromLonLat([51.3833554, 35.6770113]), zoom: 12 }),
      });
      this.resizeObserver = new ResizeObserver(() => this.map?.updateSize());
      this.resizeObserver.observe(this.mapTarget.nativeElement);
    }
    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'line',
      data: { labels: this.rows.map(chartLabel), datasets: [{
        label: this.context.type === '15' ? '(km) سرعت' : acceleration ? 'شتاب' : 'لیتر',
        data: this.rows.map(chartValue), fill: false, borderColor: '#b3b3b3', backgroundColor: '#e755ba',
        pointBackgroundColor: this.rows.map(row => acceleration && row.bCritical ? '#cf1527' : '#00b300'),
        pointBorderColor: this.rows.map(row => acceleration && row.bCritical ? '#cf1527' : '#00b300'),
        pointRadius: this.rows.map(row => acceleration ? row.bCritical ? 4 : 3 : 2), stepped: acceleration,
      }] },
      options: {
        font: { family: 'vazir' },
        responsive: true, maintainAspectRatio: false, interaction: { mode: 'nearest', intersect: false },
        onClick: (event) => {
          if (!this.map || !this.chart || !event.native) return;
          const elements = this.chart.getElementsAtEventForMode(event.native, 'nearest', { intersect: true }, true);
          if (!elements.length) return;
          const row = this.rows[elements[0].index];
          const position = row && chartPosition(row, acceleration);
          if (!position) return;
          this.mapService.removeLayerByName('marker', this.map);
          this.map.addLayer(this.mapService.createMarker([position.fLon, position.fLat], '../../assets/Img/marker5.png', 'markerId', 'marker', {}));
          this.mapService.fitMap([position], this.map);
        },
        plugins: {
          legend: { labels: { font: { family: 'vazir' } }, title: { font: { family: 'vazir' } } },
          tooltip: { titleFont: { family: 'vazir' }, bodyFont: { family: 'vazir' }, footerFont: { family: 'vazir' } },
          title: { font: { family: 'vazir' } },
          subtitle: { font: { family: 'vazir' } },
          zoom: { pan: { enabled: true, mode: 'x', threshold: 10 }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' } },
        },
        scales: {
          x: { ticks: { color: 'black', font: { family: 'vazir', size: 12 } }, title: { font: { family: 'vazir' } } },
          y: { ticks: { font: { family: 'vazir' } }, title: { font: { family: 'vazir' } } },
        },
      },
    });
  }
  private disposeVisualization(): void {
    this.chart?.destroy(); this.chart = undefined;
    this.resizeObserver?.disconnect(); this.resizeObserver = undefined;
    this.map?.setTarget(undefined); this.map?.dispose(); this.map = undefined;
  }
}
